/* eslint-disable @typescript-eslint/no-unused-vars */
// ==============================
// Imports
// ==============================

// Libraries
import { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

// Local components
import { apiGet, apiPost } from '../utils/apiClient';
import { notifyAPIClient } from '../utils/toast';

// Types
import {
  ServiceType,
  Subsubtype,
  Response,
  UpdateUserAdditionalInformationRequest,
  UpdateUserAdditionalInformationResponse,
  RegisterServiceRequest,
  RegisterServiceResponse,
  UserInfo,
  SuccessResponse,
} from '../types/ServiceAccordion.type';
import { literal, z } from 'zod';
import type { CourseLevel } from '../types/CourseStageAccordion.type';

type FormErrors = {
  email?: string;
  phone?: string;
  parent?: string;
  address?: string;
  idCard?: string;
  taxCode?: string;
  walkInCustomerName?: string;
};

type FormFinalErrors = {
  sos?: string;
  paymentMethod?: string;
  discountPrice?: string;
  amount?: string;
  subAmount?: string;
  courseLevel?: string;
  note?: string;
};

export const useServiceAccordionController = () => {
  const isWalkInCustomer =
    Number(localStorage.getItem('selectedUserId')) === 999999;

  // ==============================
  // Forms Validation
  // ==============================
  // Additional information input
  const AdditionalInfoInput = z.object({
    id: z.number().int(),
    parent:
      localStorage.getItem('selectedServiceServiceFor') === 'CHILD' // Based on the service instead of user type
        ? z.string().optional().nullable() // Optional
        : z.string().optional().nullable(),
    phone: isWalkInCustomer
      ? z.string().optional().nullable().or(z.literal(''))
      : z.string().regex(
          // /^((0|\+84)(3[2-9]|5[6|8|9]|7[0|6-9]|8[1-5]|9[0-9])[0-9]{7}|\+?[1-9]\d{1,14})$/,
          /^((0|\+84)(3[2-9]|5[6|8|9]|7[0|6-9]|8[1-6|8|9]|9[0-9])[0-9]{7}|\+?[1-9]\d{1,14})$/,
          'Please enter a valid phone number',
        ),
    address: isWalkInCustomer
      ? z.string().optional().nullable().or(z.literal(''))
      : z.string().min(1, 'Please enter address'),
    email: z
      .string({
        required_error: 'Please enter email',
      })
      .email('Invalid email format')
      .optional()
      .nullable()
      .or(z.literal('')),
    idCard:
      // localStorage.getItem('selectedUserType') === 'BUSINESS'
      //   ? z.string().regex(/^[A-Z0-9]{6,20}$/, 'Please enter a valid ID number')
      //   :
      z.string().optional().nullable().or(z.literal('')),
    taxCode:
      !isWalkInCustomer &&
      localStorage.getItem('selectedUserType') === 'BUSINESS'
        ? z.string().min(1, 'Please enter tax code')
        : z.string().optional().nullable().or(z.literal('')),
  });
  type AdditionalInfoInputType = z.infer<typeof AdditionalInfoInput>;

  // Final confirmation
  const ConfirmationInput = z.object({
    userId: z.number().int(),
    serviceId: z.number().int(),
    sos: z
      .string({
        required_error: 'Please enter start date of service',
      })
      .date('Please enter a valid date'),
    paymentMethod: z.string().min(1, 'Please select a payment method'),
    discountPrice: z.number().min(0, 'Discount price must be at least 0'),
    amount: z.number().min(1, 'Quantity must be at least 1'),
    subAmount: z
      .number()
      .min(1, 'Quantity for child amount must be at least 1'),
    courseLevel: z.string().optional(),
    note: z.string().optional(),
    walkInCustomerName: z.string().trim().max(255).nullable().optional(),
  });
  type ConfirmationInputType = z.infer<typeof ConfirmationInput>;

  // States
  const [registerLoading, setRegisterLoading] = useState(false);
  const [services, setServices] = useState<ServiceType[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogFinal, setOpenDialogFinal] = useState(false);

  const [isPhoneNotProvided, setIsPhoneNotProvided] = useState<boolean>(false);
  const [isAddressNotProvided, setIsAddressNotProvided] =
    useState<boolean>(false);

  // Additional information form state
  const [formData, setFormData] = useState({
    id: 0,
    email: '',
    phone: '',
    parent: '',
    address: '',
    idCard: '',
    taxCode: '',
    walkInCustomerName: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Confirmation form state
  const [sos, setSos] = useState<Dayjs | null>(dayjs().startOf('day'));
  const [paymentMethod, setPaymentMethod] = useState<string>('CASH');

  const [formDataFinal, setFormDataFinal] = useState({
    userId: 0,
    serviceId: 0,
    note: '',
    discountPrice: '0',
    amount: '1',
    subAmount: '1',
    courseLevel: '',
  });
  const [formFinalErrors, setFormFinalErrors] = useState<FormFinalErrors>({});
  const [courseLevelOptions, setCourseLevelOptions] = useState<CourseLevel[]>(
    [],
  );
  const [courseLevelLoading, setCourseLevelLoading] = useState(false);

  const [selectedSubtype, setSelectedSubtype] = useState<{
    serviceName: string;
    subtypeName: string;
    subsubtypes: Subsubtype[];
  } | null>(null);

  const [, setSelectedSubsubtype] = useState<{
    subsubtypeName: string;
  } | null>(null);

  // Networking
  const fetchServices = async () => {
    try {
      const res = await apiGet<Response<ServiceType>>(`/service/list`);
      // console.log('Success to fetch services:', res);
      setServices(res.data);
      // setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  // Hooks
  useEffect(() => {
    fetchServices();
    // Set dob to beg of today everytime open dialog
    if (openDialogFinal) setSos(dayjs().startOf('day'));
  }, [openDialogFinal]);

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleInputFinalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormDataFinal({ ...formDataFinal, [e.target.name]: e.target.value });
  };

  const handleCourseLevelChange = (courseLevel: string) => {
    setFormDataFinal((previous) => ({ ...previous, courseLevel }));
    setFormFinalErrors((previous) => ({
      ...previous,
      courseLevel: undefined,
    }));
  };

  // Handle not providing phone and address
  const handleNotProvidingPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    setIsPhoneNotProvided(isChecked);
    if (isChecked) {
      setFormData((prevData) => ({
        ...prevData,
        phone: '0909999999',
      }));
    } else {
      // If user uncheck, reset phone to empty string
      setFormData((prevData) => ({
        ...prevData,
        phone: '',
      }));
    }
  };
  const handleNotProvidingAddress = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = e.target.checked;

    setIsAddressNotProvided(isChecked);
    if (isChecked) {
      setFormData((prevData) => ({
        ...prevData,
        address: 'Người mua không cung cấp thông tin địa chỉ, mã số thuế',
      }));
    } else {
      // If user uncheck, reset address to empty string
      setFormData((prevData) => ({
        ...prevData,
        address: '',
      }));
    }
  };

  const handleSubtypeClick = (
    serviceName: string,
    subtypeName: string,
    subsubtypes: Subsubtype[],
  ) => {
    setSelectedSubtype({ serviceName, subtypeName, subsubtypes });
    // console.log(`Clicked on ${subtypeName} under ${serviceName}`);
  };

  const getUserInfoById = async (userId: string) => {
    if (Number(userId) === 999999) {
      setFormData({
        id: 999999,
        email: '',
        phone: '',
        parent: '',
        address: '',
        idCard: '',
        taxCode: '',
        walkInCustomerName: '',
      });
      return;
    }

    try {
      const res = await apiGet<SuccessResponse<UserInfo>>(
        `/user/info/${userId}`,
      );

      // When the admin reached here via the global search, prefill the field
      // they searched by (phone / email) if the record itself has none.
      const searchedPhone = localStorage.getItem('selectedSearchPhone') || '';
      const searchedEmail = localStorage.getItem('selectedSearchEmail') || '';

      setFormData((prevData) => ({
        ...prevData,
        email: res.data.email || searchedEmail || '',
        phone: res.data.phone || searchedPhone || '',
        parent: res.data.parent || '',
        address: res.data.address || '',
        idCard: res.data.idCard || '',
        taxCode: res.data.taxCode || '',
      }));
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchCourseLevels = async (subsubtypeId: string) => {
    if (selectedSubtype?.serviceName !== 'COURSE') {
      setCourseLevelOptions([]);
      return;
    }

    setCourseLevelLoading(true);
    try {
      const response = await apiGet<Response<CourseLevel>>(
        `/course/get-levels/${subsubtypeId}`,
      );
      setCourseLevelOptions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch course levels:', error);
      setCourseLevelOptions([]);
      notifyAPIClient('Unable to load course levels.');
    } finally {
      setCourseLevelLoading(false);
    }
  };

  const handleSubsubtypeClick = async (subsubType: Subsubtype) => {
    const subsubtypeName = subsubType.name;
    setSelectedSubsubtype({ subsubtypeName });
    // console.log(
    //   `Clicked on ${subsubtypeName} with id ${subsubType.id}, price ${subsubType.price}, service for ${subsubType.serviceFor} and duration ${subsubType.duration}`,
    // );
    // Store temporary data
    localStorage.setItem('selectedServiceName', subsubtypeName);
    localStorage.setItem('selectedServiceId', subsubType.id);
    localStorage.setItem('selectedServiceServiceFor', subsubType.serviceFor);
    localStorage.setItem('selectedServicePrice', subsubType.price.toString());
    setFormDataFinal((previous) => ({ ...previous, courseLevel: '' }));
    setFormFinalErrors({});
    if (subsubType.serviceFor === 'GROUP') {
      localStorage.setItem(
        'selectedServicePriceForChild',
        (subsubType.priceForChild || 0).toString(),
      );
    }
    getUserInfoById(localStorage.getItem('selectedUserId') as string);
    await fetchCourseLevels(subsubType.id);

    // Confirmation
    handleDialogOpen();
  };

  const handleRegister = (userId: string, serviceName: string) => {
    // toast(`Register service ${serviceName} for userId ${userId} successfully.`);
    // console.log(
    //   `Register service ${serviceName} for userId ${userId} successfully.`,
    // );
    // Update additional information
    handleUpdateUserAdditionalInformation(userId);
  };

  const handleUpdateUserAdditionalInformation = async (userId: string) => {
    formData.id = parseInt(userId as string, 10);

    if (formData.id === 999999) {
      const walkInCustomerNameResult = z
        .string()
        .trim()
        .max(255, 'Walk-in customer name must be at most 255 characters')
        .safeParse(formData.walkInCustomerName);

      if (!walkInCustomerNameResult.success) {
        setFormErrors({
          walkInCustomerName: walkInCustomerNameResult.error.errors[0]?.message,
        });
        return;
      }

      setFormErrors({});
      setOpenDialog(false);
      handleDialogFinalOpen();
      return;
    }

    const payload: UpdateUserAdditionalInformationRequest = {
      id: formData.id,
      email: formData.email,
      phone: formData.phone,
      parent: formData.parent,
      address: formData.address,
      idCard: formData.idCard,
      taxCode: formData.taxCode,
    };

    try {
      // Validate form data
      const validatedData = AdditionalInfoInput.parse(payload);

      const res = await apiPost<
        UpdateUserAdditionalInformationResponse,
        AdditionalInfoInputType
      >('/user/update', validatedData);

      notifyAPIClient(res.message);
      setOpenDialog(false);
      handleDialogFinalOpen();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormErrors;
          fieldErrors[field] = err.message;
        });
        setFormErrors(fieldErrors);
      } else {
        console.error(error);
        notifyAPIClient(
          'An unexpected error occurred while processing your request.',
        );
      }
    }
  };

  const handleRegisterUserService = async () => {
    setRegisterLoading(true);
    const requiresCourseLevel =
      selectedSubtype?.serviceName === 'COURSE' && courseLevelOptions.length > 0;
    if (requiresCourseLevel && !formDataFinal.courseLevel.trim()) {
      setFormFinalErrors({ courseLevel: 'Please select a course level.' });
      setRegisterLoading(false);
      return;
    }

    const payload: RegisterServiceRequest = {
      ...formDataFinal,
      sos: sos?.format('YYYY-MM-DD'),
      discountPrice: Number(formDataFinal.discountPrice.replace(/,/g, '') || 0),
      amount: Number(formDataFinal.amount || 0),
      subAmount: Number(formDataFinal.subAmount || 0),
      paymentMethod,
      courseLevel: formDataFinal.courseLevel || undefined,
      walkInCustomerName: isWalkInCustomer
        ? formData.walkInCustomerName.trim() || null
        : null,
    };

    try {
      // Validate form data
      const validatedData = ConfirmationInput.parse(payload);

      const res = await apiPost<RegisterServiceResponse, ConfirmationInputType>(
        '/registration/create',
        validatedData,
      );

      notifyAPIClient(res.message);
      setOpenDialogFinal(false);
      setFormData((prevData) => ({
        ...prevData,
        walkInCustomerName: '',
      }));
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormFinalErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormFinalErrors;
          fieldErrors[field] = err.message;
        });
        setFormFinalErrors(fieldErrors);
      } else {
        console.error(error);
        notifyAPIClient(
          'An unexpected error occurred while processing your request.',
        );
      }
    }
    setRegisterLoading(false);
  };

  // Dialog 1
  const handleDialogOpen = () => setOpenDialog(true);
  const handleDialogClose = () => {
    setOpenDialog(false);
    setFormData({
      id: 0,
      email: '',
      phone: '',
      parent: '',
      address: '',
      idCard: '',
      taxCode: '',
      walkInCustomerName: '',
    });
    setFormErrors({});
  };
  const handleDialogConfirm = () => {
    // Proceed with additional information
    const selectedUserId = localStorage.getItem('selectedUserId') || '';
    const selectedServiceName =
      localStorage.getItem('selectedServiceName') || '';

    handleRegister(selectedUserId, selectedServiceName);
  };

  // Dialog 2
  const handleDialogFinalOpen = () => {
    setOpenDialogFinal(true);
    formDataFinal.userId = parseInt(
      localStorage.getItem('selectedUserId') as string,
      10,
    );
    formDataFinal.serviceId = parseInt(
      localStorage.getItem('selectedServiceId') as string,
      10,
    );
  };
  const handleDialogFinalClose = () => {
    setOpenDialogFinal(false);
    setFormDataFinal({
      userId: 0,
      serviceId: 0,
      note: '',
      discountPrice: '0',
      amount: '1',
      subAmount: '1',
      courseLevel: '',
    });
    setFormFinalErrors({});
    setSos(null);
    setPaymentMethod('CASH');
    setCourseLevelOptions([]);
    setCourseLevelLoading(false);
  };
  const handleDialogFinalConfirm = () => handleRegisterUserService();

  // const dataRes = data();
  const selectedServiceServiceFor =
    localStorage.getItem('selectedServiceServiceFor') || '';

  const dataRes = services;

  return {
    registerLoading,
    services,
    dataRes,
    selectedServiceServiceFor,
    selectedSubtype,
    openDialog,
    openDialogFinal,
    formData,
    formErrors,
    formDataFinal,
    formFinalErrors,
    courseLevelOptions,
    courseLevelLoading,
    sos,
    isAddressNotProvided,
    isPhoneNotProvided,
    isWalkInCustomer,
    handleNotProvidingAddress,
    handleNotProvidingPhone,
    handleInputChange,
    handleInputFinalChange,
    handleCourseLevelChange,
    handleSubtypeClick,
    handleSubsubtypeClick,
    handleDialogOpen,
    handleDialogClose,
    handleDialogConfirm,
    handleDialogFinalOpen,
    handleDialogFinalClose,
    handleDialogFinalConfirm,
    setSos,
    paymentMethod,
    setPaymentMethod,
  };
};
